import type { RoleKey } from './enums.js';

/**
 * Permission keys. Roles are bundles of permissions; the backend checks permissions (never names or roles
 * directly) and then narrows every query with a data scope (see DataScope).
 */
export const PERMISSIONS = {
  // Dashboards
  'dashboard.operations': 'Use the Operations Workspace',
  'dashboard.shipping_management': 'Use the Shipping Management dashboard',
  'dashboard.supervisor': 'Use the supervisory dashboard',
  'dashboard.finance': 'Use the finance payment queue',
  'dashboard.executive': 'Use the executive overview',

  // Clients
  'client.read': 'View clients',
  'client.write': 'Create and edit clients',
  'client.delete': 'Delete clients (clients with history are archived instead)',
  'data.import': 'Bulk import clients and addresses from Excel',
  'client.rep_paid_mark': "Mark a client's representative as paid (so the representative is never paid twice for the same client)",
  'client.rep_paid_override': 'Pay a representative again for a client already marked "rep paid" (override with a reason)',

  // Representatives & addresses
  'representative.read': 'View representatives',
  'representative.write': 'Create and edit representatives',
  'address.read': 'View address inventory',
  'address.write': 'Create and edit addresses',

  // Shipments & tracking
  'shipment.read': 'View shipments',
  'shipment.create': 'Create shipments',
  'shipment.update': 'Update shipment records',
  'shipment.assign': 'Assign shipments to employees',
  'shipment.verify_close': 'Verify completion and close shipments',
  'tracking.manual_entry': 'Record manual tracking corrections',
  'label.purchase': 'Record shipping labels (carrier, tracking number, cost, label file) and buy them through a connected label provider',

  // Tasks
  'task.read': 'View tasks',
  'task.create': 'Create tasks',
  'task.update': 'Update tasks',
  'task.assign': 'Assign or reassign tasks to others',

  // Contracts
  'contract.read': 'View contracts',
  'contract.write': 'Prepare and update contracts',
  'contract.review': 'Review and approve contracts',
  'contract.send': 'Prepare representative agreements from the template, send them for e-signature and countersign for the company',
  'contract.template': 'Manage representative contract templates and the company details printed on them',

  // Client lease agreements (commercial / residential)
  'lease.read': 'View client lease agreements',
  'lease.write': 'Create and edit client lease agreements, generate PDFs, record signatures, terminate and renew',
  'lease.template': 'Manage lease templates and the default landlord',

  // Bank calls & communications
  'bank_call.read': 'View bank call records',
  'bank_call.write': 'Record bank calls',
  'communication.read': 'View communication logs',
  'communication.write': 'Record communications',

  // Tickets
  'ticket.read': 'View tickets',
  'ticket.create': 'Create tickets',
  'ticket.update': 'Work tickets (messages, status, next action)',
  'ticket.assign': 'Assign tickets',
  'ticket.escalate': 'Escalate tickets to the next level',
  'ticket.resolve': 'Resolve tickets',
  'ticket.reopen': 'Reopen tickets',
  'ticket.close': 'Verify and close resolved tickets',
  'ticket.level4': 'Access executive-level (Level 4) escalations',

  // Payments
  'payment.read': 'View payment requests',
  'payment.submit': 'Submit payment requests',
  'payment.verify': 'Review and verify payment requests',
  'payment.authorize': 'Authorize payment requests',
  'payment.process': 'Record payment processing and confirmations',

  // Company finance (ledger, client credit, financial reports)
  'finance.read': 'View the finance ledger, client credit and financial reports',
  'finance.write': 'Record income, expenses and client credit',
  'invoice.refund': 'Refund or void invoice payments (Authorize.net card payments and recorded wires)',

  // Checklists & reports
  'checklist.use': 'Use a personal daily checklist',
  'report.submit': 'Prepare and submit own reports',
  'report.review': 'Review reports from supervised employees',
  'analytics.read': 'View department analytics',
  'export.csv': 'Export authorized records to CSV',

  // Employees & administration
  'employee.read': 'View supervised employees and their work metrics',
  'admin.users': 'Invite, suspend and manage employee accounts',
  'announcement.manage': 'Post dashboard announcements to everyone, roles or specific people and see who acknowledged them',
  'admin.roles': 'Configure roles and permissions',
  'admin.settings': 'Configure business hours, holidays, escalation and notification rules',
  'admin.integrations': 'Configure integrations and credentials',
  'admin.audit': 'Review audit logs',

  // Address representative portal
  'rep.portal': 'Use the address representative portal (own addresses, mail, labels, drop-offs, settlements)',
  'rep.ops': 'Review representative scans, handle label requests and verify drop-offs',
  'rep.manage': 'Invite representatives, assign addresses and set service rates',
  'rep.settlement_verify': 'Verify representative weekly settlements',
  'rep.settlement_approve': 'Approve verified representative settlements',
  'rep.settlement_pay': 'Pay approved representative settlements',
  'rep.past_earnings': 'Enter what representatives are still owed for past work (paid after Jason’s approval)',

  // Client services (Credit Building and Amex Global Transfer)
  'services.read': 'View client service enrollments, phases, LLC/EIN/ITIN tracking and documents',
  'services.manage': 'Enroll clients and update service phases, checklists, LLC/EIN/ITIN tracking and documents',
  'services.banker': 'Handle banker calls for assigned client service enrollments',
  'services.global_transfer': 'See and fully manage every Amex Global Transfer client (add, bulk upload, assign, phases, documents, follow-ups, banker calls, outcome and credit limit)',
  'services.bank_applications': 'See and manage every Credit Building bank application client (Capital One, Alliant, Navy Federal, Ramp, Amex Business Checking: Access number, membership, card and loan applications, call sheet, bulk upload)',
  'idpack.request': 'Prepare ID pack requests for credit-building clients',
  'idpack.submit': 'Confirm and submit ID pack mailings to the credit bureaus',
  'opspay.request': 'Request operational payments for client services (mailing, filing, providers)',
  'opspay.approve': 'Approve or reject operational payment requests',
  'opspay.pay': 'Pay approved operational payment requests and upload the confirmation',
  'payee.manage': 'Add saved payees (vendors) for operational payments and enter their bank details (verified by someone else)',

  // Representative payouts (shipment → payment through Airwallex / Mercury)
  'payout.read': 'View representative payout requests and their status',
  'payout.request': 'Raise representative payments: verified settlement lines, weekly settlements by bank transfer and ad-hoc payments (fees, reimbursements, bonuses, advances)',
  'payout.review': 'Review payout requests (approve for Finance, reject, request correction, return)',
  'payout.finance_approve': 'Finance approval of payout requests (beneficiary, funds, fees, duplicates)',
  'payout.final_approve': 'Final approval that initiates the bank transfer (step-up authentication required)',
  'payout.beneficiary_manage': 'Add or change representative bank beneficiaries',
  'payout.beneficiary_approve': 'Verify new or changed bank details entered by someone else',
  'payout.override_acceptance': 'Mark a drop-off as accepted by the courier when tracking cannot confirm it',
  'payout.request_payroll': 'Prepare payroll payouts (salaries, contractors) to send through Airwallex for the owner\'s final approval',
  // Credit monitoring (Credit Building clients)
  'credit.read': 'View credit monitoring: scores, reports, changes and reviews of Credit Building clients',
  'credit.manage': 'Review credit reports, record credit data and resolve credit monitoring events',
  'credit.consent': 'Record or revoke clients\' credit monitoring authorizations',

  // WhatsApp agent inbox
  'whatsapp.read': 'View the WhatsApp inbox (conversations with clients, representatives and staff) without replying',
  'whatsapp.manage': 'Reply on WhatsApp, take over from the AI agent (turn it off/on), assign and close WhatsApp conversations',

  // Client portal
  'client_portal.use': 'Use the client portal (own services, progress, documents and inquiries)',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

/**
 * How widely a role may see records.
 * - assigned:   only records assigned to / created by the user
 * - department: all records in the user's departments
 * - organization: all records
 * - finance:    only payment requests that have reached finance (approved and later)
 * - representative: the address representative's own portal records
 * - client:     a client's own record and service enrollments (client portal)
 */
export type DataScope = 'none' | 'assigned' | 'department' | 'organization' | 'finance' | 'representative' | 'client';

export const SCOPE_RANK: Record<DataScope, number> = {
  none: 0,
  finance: 1,
  representative: 1,
  client: 1,
  assigned: 2,
  department: 3,
  organization: 4,
};

export interface RoleDefinition {
  key: RoleKey;
  scope: DataScope;
  permissions: Permission[];
}

const coordinator: Permission[] = [
  'dashboard.operations',
  'client.read',
  'client.write',
  'representative.read',
  'address.read',
  'shipment.read',
  'shipment.update',
  'tracking.manual_entry',
  'label.purchase',
  'task.read',
  'task.create',
  'task.update',
  'contract.read',
  'contract.write',
  // Representative agreements: Paul prepares them, sends them for e-signature and countersigns (Melina and Jason inherit).
  'contract.send',
  // Client lease agreements (Paul; Melina and Jason inherit them through their roles).
  'lease.read',
  'lease.write',
  'bank_call.read',
  'bank_call.write',
  'communication.read',
  'communication.write',
  'ticket.read',
  'ticket.create',
  'ticket.update',
  'ticket.escalate',
  'ticket.resolve',
  'payment.read',
  'payment.submit',
  'checklist.use',
  'report.submit',
  'rep.ops',
  'payout.read',
  'payout.request',
  'rep.past_earnings',
  // Paul enters a representative's Airwallex bank details with a payment (verified by Jason, never by himself).
  'payout.beneficiary_manage',
  'whatsapp.manage',
  // Paul (and Melina / Jason through their roles) marks clients whose representative was already paid.
  'client.rep_paid_mark',
];

const manager: Permission[] = [
  ...coordinator.filter((p) => p !== 'dashboard.operations'),
  'dashboard.shipping_management',
  'representative.write',
  'rep.manage',
  'rep.settlement_verify',
  'payout.override_acceptance',
  'data.import',
  'address.write',
  'shipment.create',
  'shipment.assign',
  'shipment.verify_close',
  'task.assign',
  'contract.review',
  'ticket.assign',
  'ticket.reopen',
  'ticket.close',
  'payment.verify',
  'report.review',
  'analytics.read',
  'export.csv',
  'employee.read',
];

/** Every view in the platform (all dashboards, every *.read permission and the audit log): full transparency. */
export const TRANSPARENCY_PERMISSIONS: Permission[] = ALL_PERMISSIONS.filter((p) => p.startsWith('dashboard.') || p.endsWith('.read') || p === 'admin.audit');

/** Arju: client case manager for Credit Building and Global Transfer clients (online work, documents, ID packs). */
const caseManager: Permission[] = [
  'client.delete',
  'client.read',
  'client.write',
  'services.read',
  'services.manage',
  'services.global_transfer',
  'services.bank_applications',
  'idpack.request',
  'idpack.submit',
  'opspay.request',
  // Arju adds vendor payees with their bank details (verified by Jason or the owner, never by herself).
  'payee.manage',
  'credit.read',
  'credit.manage',
  'credit.consent',
  // Arju prepares the clients' lease agreements.
  'lease.read',
  'lease.write',
  'payment.read',
  'task.read',
  'task.create',
  'task.update',
  'bank_call.read',
  'bank_call.write',
  'communication.read',
  'communication.write',
  'ticket.read',
  'ticket.create',
  'ticket.update',
  'ticket.escalate',
  'ticket.resolve',
  'checklist.use',
  'report.submit',
  'whatsapp.manage',
];

/** Paul: banker calls and telephone follow-ups for the client service enrollments assigned to him, and all Global Transfer clients. */
const bankerSpecialist: Permission[] = [
  'client.read',
  'services.banker',
  // Paul works every Global Transfer client end to end (he makes the calls); Credit Building stays assigned-only.
  'services.global_transfer',
  // Paul calls the bank representatives for Credit Building clients: he sees and edits every bank application.
  'services.bank_applications',
  'bank_call.read',
  'bank_call.write',
  'communication.read',
  'communication.write',
  'task.read',
  'task.update',
  'ticket.read',
  'ticket.update',
  'checklist.use',
  'report.submit',
];

const supervisor: Permission[] = [
  ...new Set<Permission>([
    ...manager.filter((p) => p !== 'dashboard.shipping_management'),
    'dashboard.supervisor',
    'client.delete',
    'dashboard.shipping_management',
    'payment.authorize',
    'rep.settlement_approve',
    'services.read',
    'services.manage',
    'opspay.approve',
    'payout.review',
    // Jason (and the owner) may knowingly pay a representative again for a client already marked "rep paid".
    'client.rep_paid_override',
    'announcement.manage',
    'payout.beneficiary_manage',
    'payout.beneficiary_approve',
    'credit.read',
    'credit.manage',
    'credit.consent',
    // Jason manages the lease templates (with the owner).
    'lease.template',
    // Jason manages the representative contract templates and company details (with the owner).
    'contract.template',
    ...TRANSPARENCY_PERMISSIONS,
  ]),
];

export const DEFAULT_ROLES: RoleDefinition[] = [
  { key: 'operations_coordinator', scope: 'assigned', permissions: coordinator },
  { key: 'shipping_operations_manager', scope: 'department', permissions: manager },
  { key: 'operations_supervisor', scope: 'organization', permissions: supervisor },
  {
    key: 'finance_payment_processor',
    scope: 'finance',
    permissions: ['dashboard.finance', 'payment.read', 'payment.process', 'finance.read', 'finance.write', 'client.read', 'task.read', 'task.update', 'checklist.use', 'report.submit', 'rep.settlement_pay', 'opspay.pay', 'payee.manage', 'payout.read', 'payout.finance_approve', 'payout.request_payroll', 'payout.beneficiary_manage', 'invoice.refund', 'client.write', 'data.import', 'whatsapp.read', 'lease.read'],
  },
  {
    key: 'executive_oversight',
    scope: 'organization',
    permissions: [
      'dashboard.executive',
      'payout.read',
      'services.read',
      'credit.read',
      'opspay.approve',
      'finance.read',
      'finance.write',
      'dashboard.supervisor',
      'client.read',
      'client.write',
      'representative.read',
      'representative.write',
      'address.read',
      'address.write',
      'data.import',
      'shipment.read',
      'shipment.create',
      'shipment.update',
      'label.purchase',
      'task.read',
      'task.create',
      'task.update',
      'task.assign',
      'contract.read',
      'contract.write',
      'bank_call.read',
      'communication.read',
      'ticket.read',
      'ticket.update',
      'ticket.assign',
      'ticket.resolve',
      'ticket.reopen',
      'ticket.close',
      'ticket.level4',
      'payment.read',
      'payment.authorize',
      'report.review',
      'analytics.read',
      'export.csv',
      'employee.read',
      'whatsapp.read',
    ],
  },
  {
    // Address representatives only use their own portal; every query is limited to their own records server-side.
    key: 'address_representative',
    scope: 'representative',
    permissions: ['rep.portal'],
  },
  { key: 'client_case_manager', scope: 'assigned', permissions: caseManager },
  { key: 'banker_call_specialist', scope: 'assigned', permissions: bankerSpecialist },
  {
    // Clients only use the client portal; every query is limited to their own client record server-side.
    key: 'client',
    scope: 'client',
    permissions: ['client_portal.use'],
  },
  {
    // The administrator (Aman) has every permission across all departments; also enforced at sign-in.
    key: 'system_administrator',
    scope: 'organization',
    permissions: ALL_PERMISSIONS,
  },
];

export function defaultRole(key: RoleKey): RoleDefinition {
  const r = DEFAULT_ROLES.find((d) => d.key === key);
  if (!r) throw new Error(`Unknown role ${key}`);
  return r;
}
